<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import ResultsExplorer from "../components/content/results/ResultsExplorer.svelte";
  import { Splitpanes, Pane } from "svelte-splitpanes";
  import { getActiveDrawer, setActiveDrawer, setDrawerVisibility } from "../services/stores/side-drawer-store.js";
  import { selectedObjects } from "../services/stores/selected-objects-store.js";
  import BrowseInfoContent from "../components/content/general/BrowseInfoContent.svelte";
  import SearchInfoContent from "../components/content/general/SearchInfoContent.svelte";
  import ProjectsContent from "../components/content/project/ProjectsContent.svelte";
  import DocumentsContent from "../components/content/document/DocumentsContent.svelte";
  import { userStore } from "../services/stores/user-store.js";
  import { saveUserMetadata } from "../services/api/data-handler.js";
  import { get } from "svelte/store";
  import { logError, logInfo, logWarning } from "../services/logging/log-handler.js";

  // Subscribe to userStore for reactivity
  $: $userStore;

  export let id: string; // needs to be id, because main.svelte uses id for the route

  let currentExplorer: string = "browse";
  let currentContent: string = "browseinfo";
  let sessionToLoad: string | null = null;

  const MIN_WIDTH_REM = 20;

  // Initialize with default calculated value immediately
  function calculateDefaultPosition(): number {
    const viewportWidth = window.innerWidth - 40; // 40px for drawer width
    const remInPixels = 16; // Base font size
    const minWidthPixels = MIN_WIDTH_REM * remInPixels;
    return (minWidthPixels / viewportWidth) * 100;
  }

  let splitPosition: number = calculateDefaultPosition();
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let hasLoadedPosition = false;
  let lastSavedPosition: number | null = null; // Track last saved to avoid duplicate saves
  let pendingSave = false; // Track if there's a pending save

  // Load splitter position from user metadata when user store is ready
  $: {
    if ($userStore.user && !hasLoadedPosition) {
      if ($userStore.user.metadata?.results_explorer_splitter !== undefined) {
        const saved = $userStore.user.metadata.results_explorer_splitter;
        // Handle both number and object formats (for backward compatibility)
        let sizeToUse: number | null = null;
        if (typeof saved === "number") {
          sizeToUse = saved;
        } else if (typeof saved === "object" && saved !== null && "size" in saved && typeof saved.size === "number") {
          sizeToUse = saved.size;
        }

        if (sizeToUse !== null && sizeToUse >= 10 && sizeToUse <= 90) {
          splitPosition = sizeToUse;
          lastSavedPosition = sizeToUse;
          hasLoadedPosition = true;
        } else {
          hasLoadedPosition = true;
        }
      } else {
        hasLoadedPosition = true;
      }
    }
  }

  // Actually perform the save
  async function doSave() {
    // Skip if position hasn't changed from last save
    if (lastSavedPosition !== null && Math.abs(splitPosition - lastSavedPosition) < 0.5) {
      pendingSave = false;
      return;
    }
    
    try {
      const store = get(userStore);
      if (!store.user) return;
      const newMetadata = {
        ...store.user.metadata,
        results_explorer_splitter: splitPosition,
      };
      await saveUserMetadata(newMetadata);
      lastSavedPosition = splitPosition;
    } catch (error) {
      logError(error, "ViewResults", undefined, { message: "Error saving splitter position" });
    } finally {
      pendingSave = false;
    }
  }

  // Save splitter position to user metadata (debounced)
  function saveSplitterPosition() {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    pendingSave = true;
    saveTimeout = setTimeout(() => {
      doSave();
      saveTimeout = null;
    }, 1000); // Debounce by 1000ms (increased from 500ms)
  }

  // Handle splitter resize
  function handleSplitterResized(event: CustomEvent) {
    const panes = event.detail as Array<{ size: number; min?: number; max?: number }>;
    if (panes && Array.isArray(panes) && panes.length > 0 && panes[0]?.size !== undefined) {
      splitPosition = panes[0].size;
      saveSplitterPosition();
    }
  }

  onMount(async () => {
    setDrawerVisibility("selected-documents", true);
  });

  onDestroy(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    // Save immediately on destroy if there's a pending save
    if (pendingSave) {
      doSave();
    }
  });

  onDestroy(() => {
    // Clean up drawer state
    const activeDrawer = getActiveDrawer();
    if (activeDrawer === "selected-documents") {
      setActiveDrawer(null);
    }
    setDrawerVisibility("selected-documents", false);

    // Clean up iframe and selected documents
    if (iframe) {
      iframe.style.pointerEvents = "auto";
    }

    // Cleanup selected documents and projects
    selectedObjects.clear();
  });

  $: {
    // Get all unique project IDs from selected documents
    const projectsWithSelectedDocs = new Set($selectedObjects.documents.map((doc) => doc.project_id));

    // Get all project IDs that are fully selected
    const fullySelectedProjectIds = new Set($selectedObjects.projects.map((p) => p.project_id));

    // Show ProjectsContent only if:
    // 1. There are selected documents, AND
    // 2. All projects with selected documents are fully selected
    const shouldShowProjects = projectsWithSelectedDocs.size > 0 && Array.from(projectsWithSelectedDocs).every((projectId) => fullySelectedProjectIds.has(projectId));

    if (shouldShowProjects) {
      currentContent = "projects";
    } else if ($selectedObjects.documents.length > 0) {
      currentContent = "documents";
    } else if (sessionToLoad && (currentContent === "browseinfo" || currentContent === "searchinfo")) {
      // If a session is being loaded but we're showing info views, default to projects view
      // The session loading will determine the correct view once selectedObjects is updated
      currentContent = "projects";
    } else if (!sessionToLoad) {
      // Only show info views if no session is loading
      if (currentExplorer === "browse") {
        currentContent = "browseinfo";
      } else {
        currentContent = "searchinfo";
      }
    }
  }

  let iframe: HTMLIFrameElement | null = null;
  let isResizing = false;

  function disableIframePointerEvents() {
    isResizing = true;
    if (iframe) {
      iframe.style.pointerEvents = "none";
    }
  }

  function enableIframePointerEvents() {
    isResizing = false;
    setTimeout(() => {
      if (!isResizing && iframe) {
        iframe.style.pointerEvents = "auto";
      }
    }, 100);
  }

  function handleContentChange(event: CustomEvent<string>) {
    currentExplorer = event.detail;
    if ($selectedObjects.documents.length === 0 && $selectedObjects.projects.length === 0) {
      currentContent = currentExplorer === "browse" ? "browseinfo" : "searchinfo";
    }
  }

  function handleOpenChatSession(event: CustomEvent) {
    // When a chat session is clicked, set session to load
    // The reactive logic will determine which view to show based on what gets restored to selectedObjects
    const { sessionId, sessionName } = event.detail;
    sessionToLoad = sessionId;
  }
</script>

<div class="component-container flex flex-col">
  <Splitpanes
    class="my-theme"
    on:resize={disableIframePointerEvents}
    on:resized={(e) => {
      enableIframePointerEvents();
      handleSplitterResized(e);
    }}
  >
    <Pane size={splitPosition} minSize={((MIN_WIDTH_REM * 16) / window.innerWidth) * 100}>
      <ResultsExplorer customer_id={id} on:showContent={handleContentChange} on:openChatSession={handleOpenChatSession} />
    </Pane>
    <Pane>
      {#key currentContent}
        {#if currentContent === "documents"}
          <DocumentsContent customer_id={id} iframeProp={iframe} sessionToLoad={sessionToLoad} on:sessionLoaded={() => sessionToLoad = null} />
        {:else if currentContent === "projects"}
          <ProjectsContent customer_id={id} sessionToLoad={sessionToLoad} on:sessionLoaded={() => sessionToLoad = null} />
        {:else if currentContent === "browseinfo"}
          <BrowseInfoContent />
        {:else if currentContent === "searchinfo"}
          <SearchInfoContent />
        {/if}
      {/key}
    </Pane>
  </Splitpanes>
</div>
